import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getOptions, getSentiment, fmtNum, fmtPrice } from "@/lib/api";
import { TrendingUp, TrendingDown, Target, ShieldAlert, Activity, Sparkles, Loader2 } from "lucide-react";
import { TickerChart } from "@/components/TickerChart";
import { StrategyPanel } from "@/components/StrategyPanel";

const Stat = ({ label, value, mono = true, color = "text-white" }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-widest text-tertiary font-mono">{label}</span>
    <span className={`text-sm ${mono ? "font-mono" : ""} ${color}`}>{value}</span>
  </div>
);

const AISentimentCard = ({ symbol }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try { setData(await getSentiment(symbol)); } finally { setLoading(false); }
  };
  const bull = data?.sentiment === "bullish";
  return (
    <div className="relative overflow-hidden bg-surface border border-border rounded-lg grain" data-testid="ai-sentiment-card">
      <div className="p-4 relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={15} className="text-ai" />
            <h4 className="text-sm font-semibold uppercase tracking-[0.1em] text-white">AI Sentiment</h4>
          </div>
          {data?.ai === false && <span className="text-[9px] font-mono text-tertiary uppercase">heuristic</span>}
        </div>
        {!data && !loading && (
          <button data-testid="run-ai-sentiment" onClick={run}
            className="w-full bg-ai/10 hover:bg-ai/20 border border-ai/30 text-ai font-mono text-xs py-2.5 rounded transition-colors">
            Analyze {symbol} news & sentiment
          </button>
        )}
        {loading && (
          <div className="flex items-center gap-2 text-ai font-mono text-xs py-4 justify-center">
            <Loader2 size={14} className="animate-spin" /> Analyzing headlines…
          </div>
        )}
        {data && (
          <div className="space-y-3 fade-up">
            <div className="flex items-center gap-3">
              <span className={`font-mono text-xs uppercase px-2 py-1 rounded border ${bull ? "bg-bull/10 text-bull border-bull/25" : data.sentiment === "bearish" ? "bg-bear/10 text-bear border-bear/25" : "bg-surface-2 text-secondary border-border"}`}>
                {data.sentiment}
              </span>
              <span className={`font-mono text-sm font-bold ${data.recommendation === "CALL" ? "text-bull" : "text-bear"}`}>
                → {data.recommendation}
              </span>
            </div>
            <p className="text-xs text-secondary leading-relaxed">{data.rationale}</p>
            <div className="space-y-1 pt-1 border-t border-border-subtle">
              {data.headlines?.slice(0, 3).map((h, i) => (
                <p key={i} className="text-[11px] text-tertiary leading-snug">• {h}</p>
              ))}
            </div>
            <button data-testid="rerun-ai-sentiment" onClick={run} className="text-[10px] font-mono text-ai/70 hover:text-ai uppercase">Re-analyze</button>
          </div>
        )}
      </div>
    </div>
  );
};

export const OptionsPanel = ({ symbol }) => {
  const [side, setSide] = useState("calls");
  const { data, isLoading } = useQuery({
    queryKey: ["options", symbol],
    queryFn: () => getOptions(symbol),
    refetchInterval: 20000,
    enabled: !!symbol,
  });

  if (!symbol) return (
    <div className="bg-surface border border-border rounded-lg p-8 text-center text-tertiary font-mono text-xs" data-testid="options-empty">
      Select a ticker from the scanner to view its options analysis.
    </div>
  );
  if (isLoading || !data) return (
    <div className="bg-surface border border-border rounded-lg p-8 text-center text-tertiary font-mono text-xs">Loading {symbol} options…</div>
  );

  const rec = data.recommendation;
  const chain = data.chain;
  const isCall = rec.direction === "CALL";
  const rows = side === "calls" ? chain.calls : chain.puts;

  return (
    <div className="space-y-4" data-testid="options-panel">
      <TickerChart symbol={symbol} />
      {/* Recommendation */}
      <div className={`relative overflow-hidden bg-surface border rounded-lg ${isCall ? "border-bull/30" : "border-bear/30"}`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-lg font-bold text-white">{symbol}</span>
                <span className="text-xs text-tertiary">${fmtPrice(chain.spot)}</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-tertiary font-mono mt-0.5">Trade Recommendation</div>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded font-bold ${isCall ? "bg-bull text-black" : "bg-bear text-white"}`}>
              {isCall ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              <span className="font-mono text-sm">{rec.direction}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 h-1.5 bg-surface-2 rounded overflow-hidden">
              <div className={`h-full ${isCall ? "bg-bull" : "bg-bear"}`} style={{ width: `${rec.confidence}%` }} />
            </div>
            <span className="font-mono text-xs text-secondary">{rec.confidence}% conf.</span>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <Stat label="Strike" value={`$${rec.contract.strike}`} />
            <Stat label="Prem" value={`$${rec.contract.price}`} />
            <Stat label="IV" value={`${(rec.iv * 100).toFixed(0)}%`} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 p-3 bg-app/50 rounded border border-border-subtle">
            <Stat label="Delta" value={rec.delta} color="text-white" />
            <Stat label="Gamma" value={rec.gamma} color="text-ai" />
            <Stat label="Theta" value={rec.theta} color="text-warn" />
            <Stat label="Vega" value={rec.vega} color="text-white" />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="flex items-center gap-1.5 p-2 rounded bg-app/50 border border-border-subtle">
              <Activity size={13} className="text-neutral" />
              <div><div className="text-[9px] uppercase text-tertiary font-mono">Entry</div><div className="font-mono text-xs text-white">${fmtPrice(rec.entry)}</div></div>
            </div>
            <div className="flex items-center gap-1.5 p-2 rounded bg-bull/5 border border-bull/20">
              <Target size={13} className="text-bull" />
              <div><div className="text-[9px] uppercase text-tertiary font-mono">Target</div><div className="font-mono text-xs text-bull">${fmtPrice(rec.target)}</div></div>
            </div>
            <div className="flex items-center gap-1.5 p-2 rounded bg-bear/5 border border-bear/20">
              <ShieldAlert size={13} className="text-bear" />
              <div><div className="text-[9px] uppercase text-tertiary font-mono">Stop</div><div className="font-mono text-xs text-bear">${fmtPrice(rec.stop)}</div></div>
            </div>
          </div>
        </div>
      </div>

      <AISentimentCard symbol={symbol} />

      {/* Options chain */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold uppercase tracking-[0.1em] text-white">Options Chain</h4>
            {chain.source === "polygon" ? (
              <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-bull/10 text-bull border border-bull/25">Live · Polygon</span>
            ) : (
              <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-surface-2 text-tertiary border border-border">Computed</span>
            )}
            {chain.expiry && <span className="text-[10px] font-mono text-tertiary">exp {chain.expiry}</span>}
          </div>
          <div className="flex gap-1">
            <button data-testid="chain-calls" onClick={() => setSide("calls")}
              className={`text-xs font-mono px-3 py-1 rounded ${side === "calls" ? "bg-bull text-black font-bold" : "text-secondary hover:bg-surface-2"}`}>CALLS</button>
            <button data-testid="chain-puts" onClick={() => setSide("puts")}
              className={`text-xs font-mono px-3 py-1 rounded ${side === "puts" ? "bg-bear text-white font-bold" : "text-secondary hover:bg-surface-2"}`}>PUTS</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-tertiary font-mono border-b border-border">
                <th className="text-left py-2 px-3 font-normal">Strike</th>
                <th className="text-right py-2 px-3 font-normal">Prem</th>
                <th className="text-right py-2 px-3 font-normal">Delta</th>
                <th className="text-right py-2 px-3 font-normal">Gamma</th>
                <th className="text-right py-2 px-3 font-normal">Theta</th>
                <th className="text-right py-2 px-3 font-normal">OI</th>
                <th className="text-right py-2 px-3 font-normal">Vol</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const atm = Math.abs(c.strike - chain.spot) < chain.spot * 0.013;
                return (
                  <tr key={c.strike} className={`border-b border-border-subtle font-mono ${atm ? "bg-neutral/5" : ""}`}>
                    <td className="py-2 px-3 text-white font-bold">${c.strike}{atm && <span className="ml-1 text-[9px] text-neutral">ATM</span>}</td>
                    <td className="py-2 px-3 text-right text-secondary">${c.price}</td>
                    <td className="py-2 px-3 text-right text-secondary">{c.delta}</td>
                    <td className="py-2 px-3 text-right text-ai">{c.gamma}</td>
                    <td className="py-2 px-3 text-right text-warn">{c.theta}</td>
                    <td className="py-2 px-3 text-right text-secondary">{fmtNum(c.oi)}</td>
                    <td className="py-2 px-3 text-right text-secondary">{fmtNum(c.volume)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <StrategyPanel symbol={symbol} />
    </div>
  );
};
